// Enabler E7-C (ADO-2274), D-1: the ordering entry every catalogue read names.
//
// Search and browse show only the range of the entry the store selected, so each catalogue
// request carries `useSessionStore.activeEntryKey`. With no active entry there is nothing to ask
// for: a store user is sent to `/choose` first (Story 8.1's browsing gate), and for staff the entry
// follows the depot they pick. A caller that finds no entry makes no catalogue call.

import { useSessionStore } from '../auth/useSessionStore';

/** The active ordering entry's key, or `null` when none is chosen. A point-in-time read. */
export function resolveCatalogueEntryKey(): string | null {
  return useSessionStore.getState().activeEntryKey;
}

/**
 * The active ordering entry's key as a SUBSCRIBED value: the component re-renders, and any
 * effect declaring it re-runs, when the entry changes.
 */
export function useCatalogueEntryKey(): string | null {
  return useSessionStore((s) => s.activeEntryKey);
}

/**
 * Story 7.29 (ADO-2025), D-7.29-21: the active Trading Account's code as a SUBSCRIBED value, which
 * a listing and a search name so the server's narrowing gates read the account the store selected
 * (the same account the Catering switch reads). Staff name none (D-7.29-23): their entry offers no
 * account, and the staff accounts carry no account type.
 */
export function useCatalogueAccountCode(): string | null {
  return useSessionStore((s) =>
    isStaffSession(s) ? null : (s.activeTradingAccount?.customerAccountCode ?? null),
  );
}

/** Staff order on depots (`DEPOT_IDS`), store users on trading accounts. */
function isStaffSession(s: { orderingChoices: { scope: string } | null }): boolean {
  return s.orderingChoices?.scope === 'DEPOT_IDS';
}

/**
 * Whether the session is a staff user's, as a SUBSCRIBED value (Story 7.32, ADO-1115): a catalogue
 * read names no trading account for staff, the Featured Lines view included, since staff cannot be
 * priced and an account would only withhold narrowings from them (D-7.29-23).
 */
export function useIsStaffSession(): boolean {
  return useSessionStore(isStaffSession);
}
